import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { formatPrice, useLocale } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';
import { useSites } from '../sites.js';

type Status = Schemas['ServiceRequest']['status'];

/** The status in the admin pages (admin/requests.html: .badge, the tones of requests.html). */
export function RequestStatus({ status }: { status: Status }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${STATUS_TONE[status]} nodot`}>{t(`requests.status.${status}`)}</span>
  );
}

/** Tone of the status pill (requests.html): open, waiting on the customer, done, closed. */
const STATUS_TONE: Record<Status, string> = {
  new: 'acc',
  contacted: 'acc',
  quoted: 'warn',
  won: 'ok',
  lost: 'neutral',
  cancelled: 'neutral',
};

/** "Zu den Problemen" in the empty list: the problems of the one site, else the overview. */
function ToProblems() {
  const { t } = useTranslation();
  const sites = useSites();
  if (!sites.data) return null;
  const only = sites.data.items.length === 1 ? sites.data.items[0] : undefined;
  return (
    <LinkButton to={only ? `/sites/${only.id}/findings` : '/'} variant="secondary" size="sm">
      {t('requests.toProblems')}
    </LinkButton>
  );
}

/** "Meine Anfragen" (US-05, requests.html): my service requests and their status. */
export function Requests() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const timeZone = useMe().data?.account.timezone;
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ['requests', locale],
    queryFn: () => unwrap(api.GET('/service-requests', { params: { query: { locale } } })),
  });
  const cancel = useMutation({
    mutationFn: (requestId: string) =>
      unwrap(api.POST('/service-requests/{requestId}/cancel', { params: { path: { requestId } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['requests'] }),
  });

  return (
    <div className="page">
      <div className="page-title">
        <h1>{t('requests.title')}</h1>
      </div>
      <div className="acct">
        {list.isPending ? <Loading /> : null}
        {list.isError ? (
          <LoadError message={errorMessage(list.error, t)} onRetry={() => void list.refetch()} />
        ) : null}
        {cancel.isError ? <Notice kind="error">{errorMessage(cancel.error, t)}</Notice> : null}
        {list.data && list.data.items.length === 0 ? (
          <div className="empty">
            <b>{t('requests.emptyTitle')}</b>
            <span>{t('requests.empty')}</span>
            <ToProblems />
          </div>
        ) : null}
        {list.data && list.data.items.length > 0 ? (
          <>
            <p className="bodyM">{t('requests.intro')}</p>
            {list.data.items.map((r) => {
              const pay = r.payment && r.status !== 'cancelled' ? r.payment : null;
              // An order not yet paid can be withdrawn; its invoice, paid or not, stays at hand.
              const open = pay && !pay.paidAt && r.status === 'quoted';
              return (
                <article key={r.id} className="req">
                  <div>
                    <h2>{r.serviceName ?? t('request.general')}</h2>
                    <div className="meta">
                      {[r.siteName, formatDateTime(r.createdAt, i18n.language, timeZone)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <span className={`badge ${STATUS_TONE[r.status]} nodot`}>
                    {t(`requests.status.${r.status}`)}
                  </span>
                  <p className="state">
                    {/* A lost order is not waiting for the transfer any more: its invoice is
                        voided (SP-A2.75). */}
                    {pay && (pay.paidAt || r.status !== 'lost')
                      ? t(pay.paidAt ? 'order.state.paid' : `order.state.${pay.method}`)
                      : t(`requests.explain.${r.status}`)}
                  </p>
                  {r.quotedPriceCents !== null ? (
                    <p className="quote">
                      {t('requests.quote', {
                        price: formatPrice(r.quotedPriceCents, i18n.language),
                      })}
                    </p>
                  ) : null}
                  {r.message ? <blockquote className="msg">{r.message}</blockquote> : null}
                  {pay?.invoiceId || open || r.status === 'new' ? (
                    <div className="acts">
                      {pay?.invoiceId ? (
                        <LinkButton
                          to={`/billing/invoices/${pay.invoiceId}`}
                          variant="secondary"
                          size="sm"
                        >
                          {t('order.showInvoice')}
                        </LinkButton>
                      ) : null}
                      {open || r.status === 'new' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          busy={cancel.isPending && cancel.variables === r.id}
                          onClick={() => cancel.mutate(r.id)}
                        >
                          {t(open ? 'order.cancel' : 'requests.cancel')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </>
        ) : null}
      </div>
    </div>
  );
}
